import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Tooltip,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  HStack,
  VStack,
  IconButton,
  Button,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from "recharts";
import { 
  IoRestaurantOutline, 
  IoFastFoodOutline, 
  IoStatsChartOutline,
  IoInformationCircleOutline,
  IoTrophyOutline,
  IoGridOutline,
  IoAlertCircleOutline,
  IoCashOutline,
  IoTrendingDownOutline,
  IoCloseCircleOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline
} from "react-icons/io5";
import { menuService } from "../../../../services/menuService";

const ITEMS_PER_PAGE = 5;

export default function MenuPerformance({ menuData: propMenuData }) {
  const [menuData, setMenuData] = useState(propMenuData || {
    totalItems: 0,
    activeItems: 0,
    inactiveItems: 0,
    topSellingItems: [],
    topRevenueItems: [],
    lowPerformanceItems: [],
    unsoldItems: [],
    categoriesPerformance: [],
    statistics: {
      totalMenuRevenue: 0,
      averageProductPrice: 0,
      mostProfitableProduct: null
    }
  });
  const [isLoading, setIsLoading] = useState(!propMenuData);
  const [error, setError] = useState(null);
  const [combinedPage, setCombinedPage] = useState(1);
  const [sortBy, setSortBy] = useState('orderCount'); // 'orderCount' veya 'revenue'

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const barColor = useColorModeValue("#4299E1", "#63B3ED");
  const revenueBarColor = useColorModeValue("#48BB78", "#68D391");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgIcon = useColorModeValue("white", "whiteAlpha.200");
  const boxShadow = useColorModeValue(
    "0px 2px 5px rgba(0, 0, 0, 0.05)",
    "0px 2px 5px rgba(255, 255, 255, 0.05)"
  );
  const subtleText = useColorModeValue("gray.600", "gray.400");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  const cardBg = useColorModeValue("white", "navy.800");
  
  const fetchMenuAnalytics = async () => {
    try {
      setIsLoading(true);
      
      // API'den veri çek
      const data = await menuService.getMenuAnalytics();
      
      // API'den gelen veriyi işle
      setMenuData({
        totalItems: data.totalItems || 0,
        activeItems: data.activeItems || 0,
        inactiveItems: data.inactiveItems || 0,
        topSellingItems: data.topSellingItems || [],
        topRevenueItems: data.topRevenueItems || [],
        lowPerformanceItems: data.lowPerformanceItems || [],
        unsoldItems: data.unsoldItems || [],
        categoriesPerformance: data.categoriesPerformance || [],
        statistics: data.statistics || {
          totalMenuRevenue: 0,
          averageProductPrice: 0,
          mostProfitableProduct: null
        }
      });
      
      setError(null);
    } catch (err) {
      console.error("Menü analiz verileri alınırken hata oluştu:", err);
      setError("Menü performans verileri yüklenirken bir hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    // Eğer prop olarak veri geldiyse, API'den veri çekmeye gerek yok
    if (propMenuData) {
      setMenuData(propMenuData);
      return;
    }
    
    fetchMenuAnalytics();
    
    // 5 dakikada bir verileri güncelle
    const interval = setInterval(() => {
      fetchMenuAnalytics();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [propMenuData]);
  
  if (isLoading) {
    return (
      <Card>
        <Flex justify="center" align="center" direction="column" minH="200px">
          <Spinner size="xl" color="brand.500" mb={4} />
          <Text>Menü performans verileri yükleniyor...</Text>
        </Flex>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <Alert status="error" borderRadius="md">
          <AlertIcon as={IoAlertCircleOutline} />
          {error}
        </Alert>
      </Card>
    );
  }
  
  return (
    <Box>
      {/* İstatistik Kartları */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px" mb="20px">
        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Toplam Menü Geliri
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  ₺{menuData.statistics?.totalMenuRevenue?.toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) || '0.00'}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoCashOutline} color={iconColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              Tüm ürünlerden
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  En Karlı Ürün
                </StatLabel>
                <StatNumber color={textColor} fontSize="lg" mt={1}>
                  {menuData.statistics?.mostProfitableProduct?.name || 'Yok'}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoTrophyOutline} color="yellow.500" w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              ₺{menuData.statistics?.mostProfitableProduct?.revenue?.toLocaleString('tr-TR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }) || '0.00'}
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Ortalama Ürün Fiyatı
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  ₺{menuData.statistics?.averageProductPrice?.toFixed(2) || '0.00'}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoStatsChartOutline} color={iconColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              Tüm ürünler
            </StatHelpText>
          </Stat>
        </Card>

        <Card p={6} bg={cardBg}>
          <Stat>
            <Flex justify="space-between" align="start" mb={2}>
              <Box>
                <StatLabel color={textColor} fontSize="sm" fontWeight="600">
                  Aktif / Pasif Ürün
                </StatLabel>
                <StatNumber color={textColor} fontSize="2xl" mt={1}>
                  {menuData.activeItems || 0} / {menuData.inactiveItems || 0}
                </StatNumber>
              </Box>
              <Box
                bg={bgStat}
                p={3}
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={IoFastFoodOutline} color={iconColor} w="24px" h="24px" />
              </Box>
            </Flex>
            <StatHelpText color={subtleText} fontSize="xs" mb={0}>
              Toplam {menuData.totalItems} ürün
            </StatHelpText>
          </Stat>
        </Card>
      </SimpleGrid>

      {/* Birleştirilmiş Ürün Performans Tablosu */}
      {(() => {
        // Tüm ürünleri birleştir ve badge bilgilerini ekle
        // Ürün eşleştirmesi için name kullanıyoruz (backend'den _id gelmiyor)
        const topSellingMap = new Map();
        menuData.topSellingItems?.forEach((item) => {
          // Name ile eşleştirme yapıyoruz (trim ve lowercase ile normalize ediyoruz)
          const key = (item.name || '').trim().toLowerCase();
          topSellingMap.set(key, { ...item });
        });

        const topRevenueMap = new Map();
        menuData.topRevenueItems?.forEach((item) => {
          // Name ile eşleştirme yapıyoruz (trim ve lowercase ile normalize ediyoruz)
          const key = (item.name || '').trim().toLowerCase();
          topRevenueMap.set(key, { ...item });
        });

        // Tüm benzersiz ürünleri topla
        const allProductKeys = new Set([
          ...Array.from(topSellingMap.keys()),
          ...Array.from(topRevenueMap.keys())
        ]);

        const combinedProducts = Array.from(allProductKeys).map(key => {
          const sellingItem = topSellingMap.get(key);
          const revenueItem = topRevenueMap.get(key);
          
          // Her iki listede de varsa, daha detaylı olanı kullan
          const product = sellingItem || revenueItem;
          
          return {
            ...product,
            isTopSelling: !!sellingItem, // Sadece topSellingItems içindeyse true
            isTopRevenue: !!revenueItem, // Sadece topRevenueItems içindeyse true
          };
        });

        // Sıralama
        const sortedProducts = [...combinedProducts].sort((a, b) => {
          if (sortBy === 'orderCount') {
            return (b.orderCount || 0) - (a.orderCount || 0);
          } else {
            return (b.revenue || 0) - (a.revenue || 0);
          }
        });

        // Tüm listedeki ilk ürünü belirle (sayfa bazında değil, tüm liste bazında)
        const topProductName = sortedProducts.length > 0 
          ? (sortedProducts[0].name || '').trim().toLowerCase() 
          : null;

        // Sıralamaya göre badge'leri belirle
        // Sadece tüm listedeki ilk ürüne badge ver
        const productsWithBadges = sortedProducts.map((product) => {
          const productKey = (product.name || '').trim().toLowerCase();
          const isTopProduct = productKey === topProductName;
          
          return {
            ...product,
            // Sıralama "Sipariş Sayısı"na göre ise ve en üstteki ürünse "En Çok Satan" badge'i
            showTopSellingBadge: isTopProduct && sortBy === 'orderCount',
            // Sıralama "Gelir"e göre ise ve en üstteki ürünse "En Karlı" badge'i
            showTopRevenueBadge: isTopProduct && sortBy === 'revenue'
          };
        });

        const totalPages = Math.ceil(productsWithBadges.length / ITEMS_PER_PAGE);
        const startIndex = (combinedPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const paginatedItems = productsWithBadges.slice(startIndex, endIndex);

        if (productsWithBadges.length === 0) return null;

        return (
          <Card p={6} bg={cardBg} mb="20px">
            <Flex justify="space-between" align="center" mb={4}>
              <Flex align="center">
                <Icon as={IoTrophyOutline} color="yellow.500" boxSize={5} mr={2} />
                <Text color={textColor} fontSize="lg" fontWeight="700">
                  Ürün Performans Analizi
                </Text>
              </Flex>
              <HStack spacing={3}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color={subtleText}>Sırala:</Text>
                  <Button
                    size="xs"
                    onClick={() => {
                      setSortBy('orderCount');
                      setCombinedPage(1);
                    }}
                    colorScheme={sortBy === 'orderCount' ? "brand" : "gray"}
                    variant={sortBy === 'orderCount' ? "solid" : "outline"}
                  >
                    Sipariş Sayısı
                  </Button>
                  <Button
                    size="xs"
                    onClick={() => {
                      setSortBy('revenue');
                      setCombinedPage(1);
                    }}
                    colorScheme={sortBy === 'revenue' ? "brand" : "gray"}
                    variant={sortBy === 'revenue' ? "solid" : "outline"}
                  >
                    Gelir
                  </Button>
                </HStack>
                <Badge colorScheme="brand" fontSize="sm">
                  {productsWithBadges.length} Ürün
                </Badge>
              </HStack>
            </Flex>
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>Ürün Adı</Th>
                    <Th borderColor={borderColor} isNumeric>Sipariş Sayısı</Th>
                    <Th borderColor={borderColor} isNumeric>Fiyat</Th>
                    <Th borderColor={borderColor} isNumeric>Toplam Gelir</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedItems.map((item, index) => {
                    return (
                      <Tr key={item._id || item.name || index}>
                        <Td borderColor={borderColor}>
                          <Flex align="center" wrap="wrap" gap={2}>
                            {item.name}
                            <HStack spacing={1}>
                              {item.showTopSellingBadge && (
                                <Badge colorScheme="blue" fontSize="xs">
                                  En Çok Satan
                                </Badge>
                              )}
                              {item.showTopRevenueBadge && (
                                <Badge colorScheme="green" fontSize="xs">
                                  En Karlı
                                </Badge>
                              )}
                            </HStack>
                          </Flex>
                        </Td>
                        <Td isNumeric borderColor={borderColor}>{item.orderCount || 0}</Td>
                        <Td isNumeric borderColor={borderColor}>₺{item.price?.toFixed(2) || '0.00'}</Td>
                        <Td isNumeric borderColor={borderColor} fontWeight="600" color="green.500">
                          ₺{item.revenue?.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) || '0.00'}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Flex justify="space-between" align="center" mt={4} pt={4} borderTop="1px" borderColor={borderColor}>
                <Text fontSize="sm" color={subtleText}>
                  Sayfa {combinedPage} / {totalPages} ({productsWithBadges.length} ürün)
                </Text>
                <HStack spacing={2}>
                  <IconButton
                    aria-label="Önceki sayfa"
                    icon={<IoChevronBackOutline />}
                    size="sm"
                    onClick={() => setCombinedPage(prev => Math.max(1, prev - 1))}
                    isDisabled={combinedPage === 1}
                  />
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             Math.abs(page - combinedPage) <= 1;
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <Text px={2}>...</Text>}
                          <Button
                            size="sm"
                            onClick={() => setCombinedPage(page)}
                            colorScheme={combinedPage === page ? "brand" : "gray"}
                            variant={combinedPage === page ? "solid" : "outline"}
                          >
                            {page}
                          </Button>
                        </React.Fragment>
                      );
                    })}
                  <IconButton
                    aria-label="Sonraki sayfa"
                    icon={<IoChevronForwardOutline />}
                    size="sm"
                    onClick={() => setCombinedPage(prev => Math.min(totalPages, prev + 1))}
                    isDisabled={combinedPage === totalPages}
                  />
                </HStack>
              </Flex>
            )}
          </Card>
        );
      })()}

      {/* Az Satan Ürünler */}
      {(menuData.lowPerformanceItems?.length > 0 || menuData.unsoldItems?.length > 0) && (
        <Card p={6} bg={cardBg} mb="20px">
          <Flex align="center" mb={4}>
            <Icon as={IoTrendingDownOutline} color="red.500" boxSize={5} mr={2} />
            <Text color={textColor} fontSize="lg" fontWeight="700">
              Az Satan / Satılmayan Ürünler
            </Text>
          </Flex>
          
          {menuData.unsoldItems && menuData.unsoldItems.length > 0 && (
            <Box mb={6}>
              <Flex align="center" mb={3}>
                <Icon as={IoCloseCircleOutline} color="red.500" boxSize={4} mr={2} />
                <Text fontWeight="600" color="red.500">
                  Hiç Satılmayan Ürünler ({menuData.unsoldItems.length})
                </Text>
              </Flex>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Ürün Adı</Th>
                      <Th borderColor={borderColor}>Kategori</Th>
                      <Th borderColor={borderColor} isNumeric>Fiyat</Th>
                      <Th borderColor={borderColor} isNumeric>Sipariş Sayısı</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {menuData.unsoldItems.slice(0, 10).map((item, index) => (
                      <Tr key={index}>
                        <Td borderColor={borderColor}>{item.name}</Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme="gray">{item.categoryName}</Badge>
                        </Td>
                        <Td isNumeric borderColor={borderColor}>₺{item.price?.toFixed(2) || '0.00'}</Td>
                        <Td isNumeric borderColor={borderColor} color="red.500" fontWeight="600">
                          {item.orderCount || 0}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {menuData.unsoldItems.length > 10 && (
                <Text fontSize="xs" color={subtleText} mt={2} textAlign="center">
                  ... ve {menuData.unsoldItems.length - 10} ürün daha
                </Text>
              )}
            </Box>
          )}

          {menuData.lowPerformanceItems && menuData.lowPerformanceItems.length > 0 && (
            <Box>
              <Flex align="center" mb={3}>
                <Icon as={IoAlertCircleOutline} color="orange.500" boxSize={4} mr={2} />
                <Text fontWeight="600" color="orange.500">
                  Düşük Performanslı Ürünler (5 veya daha az sipariş) ({menuData.lowPerformanceItems.length})
                </Text>
              </Flex>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Ürün Adı</Th>
                      <Th borderColor={borderColor}>Kategori</Th>
                      <Th borderColor={borderColor} isNumeric>Fiyat</Th>
                      <Th borderColor={borderColor} isNumeric>Sipariş Sayısı</Th>
                      <Th borderColor={borderColor} isNumeric>Toplam Gelir</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {menuData.lowPerformanceItems.slice(0, 10).map((item, index) => (
                      <Tr key={index}>
                        <Td borderColor={borderColor}>{item.name}</Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme="gray">{item.categoryName}</Badge>
                        </Td>
                        <Td isNumeric borderColor={borderColor}>₺{item.price?.toFixed(2) || '0.00'}</Td>
                        <Td isNumeric borderColor={borderColor} color="orange.500" fontWeight="600">
                          {item.orderCount || 0}
                        </Td>
                        <Td isNumeric borderColor={borderColor}>
                          ₺{item.revenue?.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }) || '0.00'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              {menuData.lowPerformanceItems.length > 10 && (
                <Text fontSize="xs" color={subtleText} mt={2} textAlign="center">
                  ... ve {menuData.lowPerformanceItems.length - 10} ürün daha
                </Text>
              )}
            </Box>
          )}
        </Card>
      )}
      
      {/* Kategori Performansı */}
      {menuData.categoriesPerformance && menuData.categoriesPerformance.length > 0 && (
        <Card p={6} bg={cardBg}>
          <Flex align="center" mb={4}>
            <Icon as={IoGridOutline} color={iconColor} boxSize={5} mr={2} />
            <Text color={textColor} fontSize="lg" fontWeight="700">
              Kategori Performansı
            </Text>
          </Flex>
          
          <Box h="300px" minH="300px" mt={4} w="100%">
            <ResponsiveContainer width="100%" height="100%" minHeight={300}>
              <BarChart
                data={menuData.categoriesPerformance}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" orientation="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip 
                  formatter={(value, name) => {
                    if (name === 'orderCount') {
                      return [`${value} sipariş`, 'Sipariş Sayısı'];
                    }
                    if (name === 'revenue') {
                      return [`₺${value.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Gelir'];
                    }
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="orderCount" fill={barColor} name="Sipariş Sayısı" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="revenue" fill={revenueBarColor} name="Gelir (₺)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mt="20px">
            <Flex 
              align="center" 
              bg={bgStat} 
              p="16px" 
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Icon as={IoTrophyOutline} color="yellow.500" boxSize={6} mr={3} />
              <Box>
                <Text fontSize="sm" color="gray.500">En Popüler Kategori</Text>
                <Text fontSize="md" fontWeight="bold">
                  {menuData.categoriesPerformance[0]?.name || "Veri yok"}
                </Text>
                <Text fontSize="xs" color={subtleText}>
                  {menuData.categoriesPerformance[0]?.orderCount || 0} sipariş
                </Text>
              </Box>
            </Flex>
            
            <Flex 
              align="center" 
              bg={bgStat} 
              p="16px" 
              borderRadius="12px"
              boxShadow={boxShadow}
            >
              <Icon as={IoCashOutline} color="green.500" boxSize={6} mr={3} />
              <Box>
                <Text fontSize="sm" color="gray.500">En Karlı Kategori</Text>
                <Text fontSize="md" fontWeight="bold">
                  {menuData.categoriesPerformance.sort((a, b) => (b.revenue || 0) - (a.revenue || 0))[0]?.name || "Veri yok"}
                </Text>
                <Text fontSize="xs" color={subtleText}>
                  ₺{menuData.categoriesPerformance.sort((a, b) => (b.revenue || 0) - (a.revenue || 0))[0]?.revenue?.toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }) || '0.00'}
                </Text>
              </Box>
            </Flex>
          </SimpleGrid>
        </Card>
      )}
    </Box>
  );
}
